import React, { useRef, useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton,
  CircularProgress,
  Alert
} from '@mui/material';
import {
  Close as CloseIcon,
  CameraAlt as CameraIcon,
  FlipCameraAndroid as FlipIcon,
  Check as CheckIcon,
  Refresh as RefreshIcon,
  Edit as EditIcon
} from '@mui/icons-material';
import Webcam from 'react-webcam';
import PhotoEditModal from './PhotoEditModal';

interface PhotoCaptureModalProps {
  open: boolean;
  onClose: () => void;
  onCapture: (imageDataUrl: string, file: File) => void;
  allowEdit?: boolean;
}

const PhotoCaptureModal: React.FC<PhotoCaptureModalProps> = ({
  open,
  onClose,
  onCapture,
  allowEdit = true
}) => {
  const webcamRef = useRef<Webcam>(null);
  const [cameraError, setCameraError] = useState<string>('');
  const [facingMode, setFacingMode] = useState<'user' | 'environment'>('environment');
  const [cameraReady, setCameraReady] = useState(false);
  const [capturedImage, setCapturedImage] = useState<string>('');
  const [processing, setProcessing] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (webcamRef.current?.video) {
        const stream = webcamRef.current.video.srcObject as MediaStream;
        stream?.getTracks().forEach(track => track.stop());
      }
    };
  }, []);

  // Reset when modal opens/closes
  useEffect(() => {
    if (!open) {
      setCapturedImage('');
      setCameraError('');
      setCameraReady(false);
      setProcessing(false);
    }
  }, [open]);

  const toggleCamera = () => {
    setFacingMode(prev => prev === 'user' ? 'environment' : 'user');
    setCameraReady(false);
  };

  const handleCameraError = () => {
    setCameraError('Impossible d\'accéder à la caméra. Veuillez vérifier les permissions.');
  };

  const handleCameraReady = () => {
    setCameraReady(true);
    setCameraError('');
  };

  const capturePhoto = () => {
    if (!webcamRef.current) {
      console.error('Webcam ref not available');
      return;
    }

    setProcessing(true);

    try {
      // getScreenshot returns a base64 encoded string (data URL)
      const imageSrc = webcamRef.current.getScreenshot();

      if (!imageSrc) {
        console.error('Failed to capture image');
        setProcessing(false);
        return;
      }

      setCapturedImage(imageSrc);
      setProcessing(false);
    } catch (error) {
      console.error('Error capturing photo:', error);
      setProcessing(false);
      setCameraError('Erreur lors de la capture de la photo');
    }
  };

  const retakePhoto = () => {
    setCapturedImage('');
  };

  const handleEditPhoto = () => {
    setShowEditModal(true);
  };

  const handleSaveEditedPhoto = (editedImageDataUrl: string, file: File) => {
    setCapturedImage(editedImageDataUrl);
    setShowEditModal(false);
  };

  const confirmPhoto = () => {
    if (capturedImage) {
      // Convert data URL to File
      const byteString = atob(capturedImage.split(',')[1]);
      const mimeString = capturedImage.split(',')[0].split(':')[1].split(';')[0];
      const ab = new ArrayBuffer(byteString.length);
      const ia = new Uint8Array(ab);
      for (let i = 0; i < byteString.length; i++) {
        ia[i] = byteString.charCodeAt(i);
      }
      const blob = new Blob([ab], { type: mimeString });
      const file = new File([blob], `photo_${Date.now()}.jpg`, { type: 'image/jpeg' });

      onCapture(capturedImage, file);
      onClose();
    }
  };

  // If we have a captured image, show review screen
  if (capturedImage && !showEditModal) {
    return (
      <>
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
          <DialogTitle>
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Typography variant="h6">Vérifier la photo</Typography>
              <IconButton onClick={onClose} size="small">
                <CloseIcon />
              </IconButton>
            </Box>
          </DialogTitle>
          <DialogContent>
            <Box sx={{ textAlign: 'center', mb: 2 }}>
              <Box
                component="img"
                src={capturedImage}
                alt="Captured photo"
                sx={{
                  maxWidth: '100%',
                  maxHeight: '400px',
                  objectFit: 'contain',
                  border: '2px solid',
                  borderColor: 'primary.main',
                  borderRadius: 2
                }}
              />
            </Box>
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button
              onClick={retakePhoto}
              startIcon={<RefreshIcon />}
              variant="outlined"
            >
              Reprendre
            </Button>
            {allowEdit && (
              <Button
                onClick={handleEditPhoto}
                startIcon={<EditIcon />}
                variant="outlined"
                color="primary"
              >
                Éditer
              </Button>
            )}
            <Button
              onClick={confirmPhoto}
              startIcon={<CheckIcon />}
              variant="contained"
            >
              Utiliser cette photo
            </Button>
          </DialogActions>
        </Dialog>
      </>
    );
  }

  // Photo Edit Modal
  if (showEditModal && capturedImage) {
    return (
      <PhotoEditModal
        open={showEditModal}
        onClose={() => setShowEditModal(false)}
        imageSrc={capturedImage}
        onSave={handleSaveEditedPhoto}
      />
    );
  }

  // Camera View
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">Capturer une photo</Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ position: 'relative', width: '100%', aspectRatio: '4/3', bgcolor: 'black', borderRadius: 2, overflow: 'hidden' }}>
          {cameraError ? (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100%',
                p: 3,
                textAlign: 'center'
              }}
            >
              <Alert severity="error">{cameraError}</Alert>
            </Box>
          ) : (
            <>
              {/* Webcam Component */}
              <Webcam
                ref={webcamRef}
                audio={false}
                screenshotFormat="image/jpeg"
                screenshotQuality={0.92}
                videoConstraints={{
                  facingMode: facingMode,
                  width: { ideal: 1920 },
                  height: { ideal: 1080 }
                }}
                onUserMedia={handleCameraReady}
                onUserMediaError={handleCameraError}
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover'
                }}
                forceScreenshotSourceSize={true}
              />

              {/* Camera Flip Button */}
              <IconButton
                onClick={toggleCamera}
                disabled={!cameraReady}
                sx={{
                  position: 'absolute',
                  top: 10,
                  right: 10,
                  bgcolor: 'rgba(0, 0, 0, 0.5)',
                  color: 'white',
                  '&:hover': {
                    bgcolor: 'rgba(0, 0, 0, 0.7)'
                  }
                }}
              >
                <FlipIcon />
              </IconButton>

              {/* Processing Overlay */}
              {processing && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    bgcolor: 'rgba(0, 0, 0, 0.7)',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 2
                  }}
                >
                  <CircularProgress size={60} sx={{ color: 'white' }} />
                  <Typography variant="body1" sx={{ color: 'white' }}>
                    Capture en cours...
                  </Typography>
                </Box>
              )}

              {/* Capture Button Overlay */}
              {cameraReady && !processing && (
                <Box
                  sx={{
                    position: 'absolute',
                    bottom: 20,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    zIndex: 10
                  }}
                >
                  <Button
                    variant="contained"
                    size="large"
                    startIcon={<CameraIcon />}
                    onClick={capturePhoto}
                    sx={{
                      py: 1.5,
                      px: 3,
                      fontSize: '1rem',
                      fontWeight: 600,
                      borderRadius: '50px',
                      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
                      '&:hover': {
                        boxShadow: '0 6px 16px rgba(0, 0, 0, 0.4)',
                      }
                    }}
                  >
                    Capturer
                  </Button>
                </Box>
              )}
            </>
          )}
        </Box>

        {/* Camera Status */}
        {!cameraReady && !cameraError && (
          <Box sx={{ mt: 2, textAlign: 'center' }}>
            <CircularProgress size={24} />
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
              Initialisation de la caméra...
            </Typography>
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose} variant="outlined">
          Annuler
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PhotoCaptureModal;

